import { groupBy } from "lodash";
import React, { useState } from "react";
import { capitalize, Paper } from "@material-ui/core";
import MaterialTable, { Column, Options } from "@material-table/core";
import { Alert } from "@material-ui/lab";
import { Problem as ProblemType } from "../validate";
import { fields } from "../fields";
import { Problem, renderProblemText } from "./validations";

const getExpectedFormat = (problem: ProblemType): string => {
  if (problem.key === "invalid-date-format") {
    return "MM/DD/YY";
  }
  if (problem.key === "missing-contact-info") {
    return "valid@email.com";
  }
  if (problem.key.startsWith("invalid-") && problem.key.includes("email")) {
    return "valid@email.com";
  }
  if (problem.key.startsWith("invalid-") && problem.key.includes("phone")) {
    return "(XXX) XXX-XXXX";
  }

  if (problem.key === "missing-time") {
    return "12:00 AM";
  }

  if (problem.key === "missing-date-time") {
    return "MM/DD/YY";
  }

  if (problem.key === "position-order") {
    return "1,2,3";
  }

  if (problem.key === "submit-date-order") {
    return "MM/DD/YY HH:mm AM/PM";
  }

  return "-";
};

const getRowNumber = (problem: ProblemType): number => {
  if ("index" in problem.data && typeof problem.data.index === "number") {
    return problem.data.index + 2;
  }
  return 0;
};

const getValue = (problem: ProblemType): string => ("value" in problem.data ? problem.data.value : "");

type TableDataRow = {
  id: string;
  problem: ProblemType;
  currentFormat: string;
  expectedFormat: string;
  row: number;
};

type TableData = TableDataRow & {
  instances: number;
  details: Array<TableDataRow>;
};

const getProblemField = (problem: ProblemType, nonBreakingSpace = false): string => {
  if (problem.type === "global") return "-";
  const { data: { field } } = problem;
  if (fields[field]) {
    const { label } = fields[field];
    return nonBreakingSpace ? label.replace(/ /g, "\u00A0") : label;
  }
  return field ? capitalize(field) : "-";
};

const ValidationsV2: React.FC<{ problems: ProblemType[] }> = ({ problems }) => {
  const grouped = groupBy(problems, problem => problem.key);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const handleRowClick = (
    _event?: React.MouseEvent<Element, MouseEvent>,
    rowData?: TableData,
    toggleDetailPanel?: (panelIndex?: number) => void,
  ) => {
    if (rowData) {
      setExpandedRows(prev => ({
        ...prev,
        [rowData.id]: !prev[rowData.id],
      }));
      toggleDetailPanel?.();
    }
  };

  const tableData = Object.entries(grouped).map(([key, group]) => {
    const [candidate] = group;
    if (!candidate) return null;

    return {
      id: key,
      problem: candidate,
      currentFormat: getValue(candidate) || "Blank",
      expectedFormat: getExpectedFormat(candidate),
      row: getRowNumber(candidate),
      instances: group.length,
      details: group.map((problem, index) => ({
        id: `${key}-${index}`,
        problem,
        currentFormat: getValue(problem) || "Blank",
        expectedFormat: getExpectedFormat(problem),
        row: getRowNumber(problem),
      })),
    };
  }).filter(Boolean) as TableData[];

  const columns: Column<TableData>[] = [
    {
      title: "Severity",
      field: "key",
      render: (rowData) => {
        switch (rowData.problem.type) {
          case "global":
            return (<Alert severity="error">Global Error</Alert>);
          case "default":
            return (<Alert severity="warning">Warning</Alert>);
          case "record":
          default:
            return (<Alert severity="error">Error</Alert>);
        }
      },
    },
    {
      title: "Field",
      field: "field",
      render: (rowData) => getProblemField(rowData.problem, true),
    },
    {
      title: "Problem",
      field: "problem",
      // defaultGroupOrder: 0,
      render: (rowData) => <Problem problem={rowData.problem} />,
      align: "left",
    },
    {
      title: "Row",
      field: "row",
      type: "numeric" as const,
      width: 50,
      align: "left",
    },
    {
      title: "Current Value",
      field: "currentFormat",
      align: "left",
    },
    {
      title: "Expected Format",
      field: "expectedFormat",
      align: "left",
    },
    {
      title: "Instances",
      field: "instances",
      type: "numeric" as const,
      width: 50,
      align: "left",
    },
  ];

  const detailPanel = ({ rowData }: { rowData: TableData }) => (
    <MaterialTable
      title=""
      columns={[
        {
          title: "Severity",
          field: "key",
          render: (rowData) => {
            switch (rowData.problem.type) {
              case "global":
                return (<Alert severity="error">Global Error</Alert>);
              case "default":
                return (<Alert severity="warning">Warning</Alert>);
              case "record":
              default:
                return (<Alert severity="error">Error</Alert>);
            }
          },
        },
        {
          title: "Field",
          field: "field",
          render: (rowData) => getProblemField(rowData.problem, true),
        },
        { title: "Problem", field: "problem", render: (rowData: any) => <Problem problem={rowData.problem} /> },
        { title: "Row", field: "row", type: "numeric" as const, width: 50 },
        { title: "Current Format", field: "currentFormat" },
        { title: "Expected Format", field: "expectedFormat" },
        { title: "Alignment Placeholder", field: "placeholder1" },
      ]}
      data={rowData.details}
      options={{
        search: false,
        paging: false,
        toolbar: false,
        header: false,
        padding: "dense",
        detailPanelColumnAlignment: "left",
      }}
    />
  );

  const options: Options<TableData> = {
    actionsColumnIndex: -1,
    pageSize: 10,
    pageSizeOptions: [10, 20, 50],
    showFirstLastPageButtons: true,
    detailPanelColumnAlignment: "right",
    rowStyle: (rowData) => ({
      backgroundColor: expandedRows[rowData.id] ? "#f5f5f5" : "#fff",
    }),
    padding: "dense",
  };

  return (
    <MaterialTable
      title="Validation Errors"
      columns={columns}
      data={tableData}
      detailPanel={detailPanel}
      onRowClick={handleRowClick}
      options={options}
      actions={[
        {
          icon: "save_alt",
          tooltip: "Export to CSV",
          isFreeAction: true,
          onClick: () => {
            const csvContent = [
              ["Severity", "Field", "Problem", "Current Format", "Expected Format", "Row", "Instances"],
              ...tableData.map(row => row.details)
                .flatMap(details => details.map(detail => [
                  detail.problem.type === "global" ? "Global Error" : detail.problem.type === "default" ? "Warning" : "Error",
                  getProblemField(detail.problem),
                  renderProblemText(detail.problem),
                  detail.currentFormat,
                  detail.expectedFormat,
                  detail.row,
                ])),
            ].map(row => row.join(",")).join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "validation-errors.csv");
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          },
        },
      ]}
      components={{
        Container: (props) => <Paper {...props} elevation={0} style={{ width: "100%" }} />,
      }}
    />
  );
};

export default ValidationsV2;
